/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-10-20T19:00:00+05:00
Intent: One campaign status rule for the campaigns list and the dashboard campaigns table.
*/
import { isArabicLang } from 'src/app/utils/arabic-pdf.util';
import { isSdkOnlyApproved, isWebQrOnlyApproved, offAppOngoingStatus } from './campaign-publication';

/** The status a campaign is shown with, derived from its stored status and its dates. */
export function campaignDisplayStatus(item: any, now: Date = new Date()): string {
  const status = (item?.status || '').toLowerCase();

  // If status is 'completed' (prize announced from backend), return as-is
  if (status === 'completed') {
    return 'completed';
  }

  // If status is luqta_pending, return as-is
  if (status === 'luqta_pending') {
    return 'luqta_pending';
  }

  // The gateway reports a started approval as `ongoing`. Off the app it runs only on
  // the business's own surfaces, and the label says which (WebQR / SDK Ongoing).
  if (status === 'ongoing') {
    return offAppOngoingStatus(item) ?? 'ongoing';
  }

  // If status is not approved, return as-is (draft, pending, rejected)
  if (status !== 'approved') {
    return status;
  }

  // Contest is approved but not active yet - waiting to be published
  // Keyed on published_at, NOT on `active`. active means "running right now"
  // (start <= now < end), so an approved contest that HAS been published and simply
  // starts next week reported as "waiting for publish". Two separate facts, and this
  // label is about the publish one. The state itself stays — the product has a real
  // publish step (PUBLISH_CONTEST).
  const publishedAt = item.published_at ?? item.publishedAt ?? null;
  const isPublished = !!publishedAt || [true, 1, '1'].includes(item.is_visible_in_app);
  if (!isPublished) {
    return 'waiting_publish';
  }

  // Publication channel is not a clock status. A live Web QR-only campaign is
  // still Web QR approved when its start date arrives; the countdown remains separate.
  // After its end date the existing prize lifecycle takes precedence.
  // Contest is approved AND active - check dates to determine ongoing/waiting_prize
  const startDate = item.start_date ? new Date(item.start_date) : null;
  const endDate = item.end_date ? new Date(item.end_date) : null;

  // If contest has started but not ended yet -> Ongoing (WebQR / SDK Ongoing off the app)
  if (startDate && endDate && now >= startDate && now < endDate) {
    return offAppOngoingStatus(item) ?? (isWebQrOnlyApproved(item) ? 'web_approved' : 'ongoing');
  }

  if (isWebQrOnlyApproved(item)) {
    const end = item.end_date ? new Date(item.end_date).getTime() : NaN;
    if (Number.isFinite(end) && now.getTime() >= end) return 'waiting_prize';
    return isSdkOnlyApproved(item) ? 'sdk_approved' : 'web_approved';
  }

  // If contest has ended but prize not announced yet -> Waiting Prize
  // completed status comes from backend when prize is announced
  if (endDate && now >= endDate) {
    return 'waiting_prize';
  }

  // Contest approved and active but not started yet
  return 'approved';
}

/** Display label for a status from campaignDisplayStatus. */
export function campaignStatusLabel(status: string, translate: { instant(key: string): string }): string {
  const isAr = isArabicLang();
  switch (status) {
    case 'draft': return isAr ? 'مسودة' : 'Draft';
    case 'pending': return isAr ? 'قيد الانتظار' : 'Pending';
    case 'approved': return isAr ? 'معتمد' : 'Approved';
    case 'rejected': return isAr ? 'مرفوض' : 'Rejected';
    case 'completed': return isAr ? 'مكتمل' : 'Completed';
    case 'ongoing': return isAr ? 'نشط' : 'Ongoing';
    case 'waiting_prize': return isAr ? 'في انتظار الجائزة' : 'Waiting Prize';
    case 'waiting_publish': return isAr ? 'في انتظار النشر' : 'Waiting for Publish';
    case 'luqta_pending': return isAr ? 'قيد مراجعة لقطة' : 'Luqta Pending';
    case 'web_approved': return translate.instant('allContests.webQrApproved');
    case 'sdk_approved': return isAr ? 'معتمد لـ SDK' : 'SDK Approved';
    case 'webqr_ongoing': return isAr ? 'نشط على WebQR' : 'WebQR Ongoing';
    case 'sdk_ongoing': return isAr ? 'نشط على SDK' : 'SDK Ongoing';
    case 'sdk_webqr_ongoing': return isAr ? 'نشط على SDK و WebQR' : 'SDK + WebQR Ongoing';
    // Unknown backend status: show a readable label without changing its stored value.
    default: return humanizeStatus(status);
  }
}

// Convert a raw snake_case/kebab-case status into a readable Title Case label
function humanizeStatus(status: string): string {
  if (!status) return '-';
  return String(status)
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
